import {
  ActionSheetContext,
  type AddOption,
  InputSelect,
  Link,
  ListButton,
  type Option,
  Tag,
} from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchAddresses, selectAddress } from '~shared/actions/addresses';
import { updateOrder } from '~shared/actions/checkout';
import { getAddresses } from '~shared/features/addresses/selectors/getAddresses';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import { getOrder, getOrderAddressId, getSaveOrderLoading } from '~shared/features/checkout/selectors/getOrder';
import { type HomeToSignConfirmation } from '~shared/features/checkout/types';
import { useFetchNextAvailableDatesForCart } from '~shared/features/next-available-date/hooks/useFetchNextAvailableDatesForCart';
import { invalidateNextAvailableDatesQueries } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { formatStreetAddress } from '~shared/helpers/address';
import { type OriginName } from '~shared/lib/analytics/src/getOrigin';
import { exitOrderBundling } from '~shared/reducers/ui/checkoutFlow';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Address } from '~shared/types';
import { AddressFormActionSheet } from './AddressFormActionSheet';
import { addressListDescriptions } from './AddressListItem';

type Props = {
  readonly disabled?: boolean;
  readonly origin?: OriginName;
};

export const AddressSelector = ({ disabled, origin }: Props) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const addresses = useSelectorShared(getAddresses);
  const defaultAddress = useSelectorShared(getDefaultAddress);
  const orderAddressID = useSelectorShared(getOrderAddressId);
  const order = useSelectorShared(getOrder);
  const savingOrder = useSelectorShared(getSaveOrderLoading);
  const isEditingExistingOrder = useSelectorShared(getIsEditingExistingOrder);
  const fetchNextAvailableDatesForCart = useFetchNextAvailableDatesForCart();

  useEffect(() => {
    if (!addresses.length) {
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchAddresses());
    }
  }, [dispatch, addresses.length]);

  // clear the address if it isn't found
  const isValidOrderAddress = addresses.some((address) => address.id === orderAddressID);
  useEffect(() => {
    if (!addresses.length) return;
    if (!orderAddressID) return;
    // Wait for addresses to load before clearing - prevents race condition when editing orders
    // where the address might be valid but addresses haven't loaded yet
    if (isEditingExistingOrder) return;

    if (!isValidOrderAddress) {
      dispatch(updateOrder({ address_id: undefined }));
    }
  }, [dispatch, isValidOrderAddress, orderAddressID, addresses.length, isEditingExistingOrder]);

  // auto-select an address for non-pick up orders
  const defaultAddressID = defaultAddress?.id;
  useEffect(() => {
    if (!addresses.length) return;
    // Don't auto-select default address when editing an existing order, keep the original address
    if (isEditingExistingOrder) return;
    // Don't auto-select if order already has an address_id set (prevents race condition during initialization)
    if (orderAddressID) return;

    // auto-select the primary address for non-pick up orders
    if (order.delivery_method !== 'pickup' && defaultAddressID) {
      dispatch(updateOrder({ address_id: defaultAddressID }));
    }
  }, [addresses.length, defaultAddressID, dispatch, order.delivery_method, orderAddressID, isEditingExistingOrder]);

  const handleAddAddress = useCallback(() => {
    setActiveActionSheet(<AddressFormActionSheet origin={origin} />);
  }, [origin, setActiveActionSheet]);

  const options = useMemo(() => {
    const handleEditAddress = (address: Address) => () => {
      dispatch(selectAddress(address));
      setActiveActionSheet(<AddressFormActionSheet origin={origin} />);
    };

    const options =
      addresses.map((address): Option => {
        return {
          label: formatStreetAddress(address),
          value: address.id.toString(),
          radioPlacement: 'left',
          RightContent: <Link onPress={handleEditAddress(address)}>Edit</Link>,
          tag: address.primary ? <Tag label="Default Address" /> : undefined,
          tagPlacement: 'bottom',
          descriptions: addressListDescriptions(address),
        };
      }) || [];

    const addAddressOption: AddOption = {
      label: 'Add new address',
      value: 'ADD_OPTION_VALUE',
      onPress: handleAddAddress,
    };

    options.push(addAddressOption);

    return options;
  }, [addresses, dispatch, handleAddAddress, origin, setActiveActionSheet]);

  const handleSelectAddress = (value: string) => {
    const addressID = Number(value);

    // Only update the order if the address actually changed
    // This preserves fulfillment options (date, deliver_before, deliver_after) when the address doesn't change
    if (!orderAddressID || addressID !== orderAddressID) {
      // reset home to sign confirmation when order address changes
      const updatePayload: { address_id: number; home_to_sign_confirmation?: HomeToSignConfirmation } = {
        address_id: addressID,
      };
      if (orderAddressID) {
        updatePayload.home_to_sign_confirmation = undefined;
      }
      dispatch(updateOrder(updatePayload));
      invalidateNextAvailableDatesQueries(queryClient);
      void fetchNextAvailableDatesForCart();
      dispatch(exitOrderBundling());
    }
  };

  if (!addresses.length) {
    return (
      <ListButton
        onPress={handleAddAddress}
        label="Add delivery address"
        rightIcon="plus-small"
      />
    );
  }

  return (
    <InputSelect
      leftIcon="mapmarker"
      value={orderAddressID?.toString()}
      options={options}
      onValueChange={handleSelectAddress}
      placeholder="Add delivery address"
      modalTitle="Select address"
      disabled={disabled || savingOrder}
      required
      showSeparators
    />
  );
};
